import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ShieldCheck, LogOut, Users, CalendarCheck, Route, FileDown, Search, Download, StickyNote } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { api, authHeader, API } from "@/lib/api";

const STATUSES = ["New", "Contacted", "Quote Sent", "Waiting for Customer", "Converted", "Lost / Closed"];
const STATUS_COLORS = {
  "New": "bg-blue-100 text-blue-700",
  "Contacted": "bg-amber-100 text-amber-700",
  "Quote Sent": "bg-purple-100 text-purple-700",
  "Waiting for Customer": "bg-orange-100 text-orange-700",
  "Converted": "bg-emerald-100 text-emerald-700",
  "Lost / Closed": "bg-slate-200 text-slate-600",
};
const SRC_COLORS = {
  contact: "bg-blue-100 text-blue-700",
  journey: "bg-purple-100 text-purple-700",
  resource: "bg-amber-100 text-amber-700",
  booking: "bg-emerald-100 text-emerald-700",
};

function fmt(iso) {
  try { return new Date(iso).toLocaleString("en-NZ", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }); }
  catch { return iso; }
}

function StatusSelect({ value, onChange, testId }) {
  const v = STATUSES.includes(value) ? value : "New";
  return (
    <Select value={v} onValueChange={onChange}>
      <SelectTrigger data-testid={testId} className={`h-8 w-[168px] rounded-full border-0 text-xs font-semibold ${STATUS_COLORS[v] || "bg-slate-100 text-slate-600"}`}>
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        {STATUSES.map((s) => <SelectItem key={s} value={s} className="text-sm">{s}</SelectItem>)}
      </SelectContent>
    </Select>
  );
}

export default function AdminDashboard() {
  const nav = useNavigate();
  const [stats, setStats] = useState(null);
  const [leads, setLeads] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [sourceFilter, setSourceFilter] = useState("all");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [notesEdit, setNotesEdit] = useState(null); // {type, id, name, value}
  const [savingNotes, setSavingNotes] = useState(false);

  const headers = authHeader();
  const logout = () => { localStorage.removeItem("qc_admin_token"); nav("/admin"); };

  useEffect(() => {
    if (!headers.Authorization) { nav("/admin"); return; }
    const load = async () => {
      try {
        const [s, l, b] = await Promise.all([
          api.get("/admin/stats", { headers }),
          api.get("/admin/leads", { headers }),
          api.get("/admin/bookings", { headers }),
        ]);
        setStats(s.data); setLeads(l.data); setBookings(b.data);
      } catch (err) {
        if (err.response?.status === 401) { localStorage.removeItem("qc_admin_token"); nav("/admin"); }
      }
    };
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nav]);

  const withinDate = (iso) => {
    if (!iso) return true;
    const d = String(iso).slice(0, 10);
    if (dateFrom && d < dateFrom) return false;
    if (dateTo && d > dateTo) return false;
    return true;
  };

  const matchStatus = (rec) => statusFilter === "all" || (rec.status || "New") === statusFilter;

  const filteredLeads = leads.filter((l) => {
    const s = `${l.name} ${l.email} ${l.source}`.toLowerCase().includes(q.toLowerCase());
    return s && matchStatus(l) && (sourceFilter === "all" || l.source === sourceFilter) && withinDate(l.created_at);
  });
  const filteredBookings = bookings.filter((b) => {
    const s = `${b.name} ${b.email} ${b.service}`.toLowerCase().includes(q.toLowerCase());
    return s && matchStatus(b) && withinDate(b.created_at);
  });

  const updateStatus = async (type, id, status) => {
    try {
      await api.patch(`/admin/${type}/${id}`, { status }, { headers });
      const setter = type === "leads" ? setLeads : setBookings;
      setter((arr) => arr.map((r) => (r.id === id ? { ...r, status } : r)));
      toast.success(`Status updated to ${status}`);
    } catch { toast.error("Could not update status"); }
  };

  const saveNotes = async () => {
    if (!notesEdit) return;
    setSavingNotes(true);
    try {
      await api.patch(`/admin/${notesEdit.type}/${notesEdit.id}`, { internal_notes: notesEdit.value }, { headers });
      const setter = notesEdit.type === "leads" ? setLeads : setBookings;
      setter((arr) => arr.map((r) => (r.id === notesEdit.id ? { ...r, internal_notes: notesEdit.value } : r)));
      toast.success("Notes saved");
      setNotesEdit(null);
    } catch { toast.error("Could not save notes"); }
    finally { setSavingNotes(false); }
  };

  const exportCsv = async (type) => {
    try {
      const res = await fetch(`${API}/admin/${type}/export`, { headers });
      if (!res.ok) throw new Error();
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `quantum-cover-${type}.csv`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success(`${type === "leads" ? "Leads" : "Bookings"} exported`);
    } catch { toast.error("Export failed"); }
  };

  const resetFilters = () => { setQ(""); setStatusFilter("all"); setSourceFilter("all"); setDateFrom(""); setDateTo(""); };

  const STAT_CARDS = stats ? [
    { icon: Users, label: "Total Leads", value: stats.leads },
    { icon: CalendarCheck, label: "Bookings", value: stats.bookings },
    { icon: Route, label: "Journeys", value: stats.journeys },
    { icon: FileDown, label: "Resource Requests", value: stats.resources },
  ] : [];

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="bg-[#0B3D2E] text-white">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <span className="grid place-items-center h-9 w-9 rounded-xl bg-white/10 text-emerald-400"><ShieldCheck size={20} /></span>
            <span className="font-display font-semibold">Quantum Cover · Admin</span>
          </div>
          <Button onClick={logout} data-testid="admin-logout" variant="ghost" className="text-slate-300 hover:text-white hover:bg-white/10 rounded-full"><LogOut size={16} className="mr-2" /> Logout</Button>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-5 sm:px-8 py-8">
        <h1 className="font-display text-2xl font-semibold text-[#0B3D2E]">Dashboard</h1>
        <p className="text-slate-500 text-sm">Leads and bookings captured across the platform.</p>

        <div className="mt-6 grid grid-cols-2 lg:grid-cols-4 gap-4">
          {STAT_CARDS.map((s) => (
            <div key={s.label} data-testid="stat-card" className="bg-white rounded-2xl border border-slate-200 p-5">
              <span className="grid place-items-center h-10 w-10 rounded-xl bg-emerald-50 text-emerald-600 mb-3"><s.icon size={20} /></span>
              <p className="font-display text-3xl font-bold text-[#0B3D2E]">{s.value}</p>
              <p className="text-sm text-slate-500">{s.label}</p>
            </div>
          ))}
        </div>

        {stats?.pipeline && (
          <div className="mt-6" data-testid="pipeline-funnel">
            <p className="text-xs uppercase tracking-[0.15em] font-semibold text-slate-400 mb-3">Sales pipeline · click to filter</p>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
              {STATUSES.map((s) => {
                const active = statusFilter === s;
                const slug = s.toLowerCase().replace(/[^a-z]+/g, "-");
                return (
                  <button
                    key={s}
                    data-testid={`funnel-${slug}`}
                    onClick={() => setStatusFilter(active ? "all" : s)}
                    className={`text-left rounded-2xl border p-4 bg-white transition-[box-shadow,border-color,transform] hover:-translate-y-0.5 ${active ? "border-emerald-500 ring-2 ring-emerald-500/40" : "border-slate-200 hover:border-emerald-300"}`}
                  >
                    <span className={`inline-block rounded-full px-2.5 py-0.5 text-[11px] font-semibold ${STATUS_COLORS[s]}`}>{s}</span>
                    <p className="mt-2 font-display text-2xl font-bold text-[#0B3D2E]">{stats.pipeline[s] ?? 0}</p>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        <Tabs defaultValue="leads" className="mt-8">
          <TabsList className="rounded-full bg-slate-100 p-1">
            <TabsTrigger value="leads" data-testid="tab-leads" className="rounded-full data-[state=active]:bg-white">Leads</TabsTrigger>
            <TabsTrigger value="bookings" data-testid="tab-bookings" className="rounded-full data-[state=active]:bg-white">Bookings</TabsTrigger>
          </TabsList>

          {/* Filter bar (shared) */}
          <div className="mt-5 flex flex-wrap items-end gap-3">
            <div className="relative">
              <Search size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search…" data-testid="lead-search" className="rounded-full pl-10 h-11 bg-white w-56" />
            </div>
            <div>
              <label className="block text-xs text-slate-500 mb-1 ml-1">Status</label>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger data-testid="filter-status" className="h-11 w-48 rounded-full bg-white"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All statuses</SelectItem>
                  {STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="block text-xs text-slate-500 mb-1 ml-1">Source</label>
              <Select value={sourceFilter} onValueChange={setSourceFilter}>
                <SelectTrigger data-testid="filter-source" className="h-11 w-40 rounded-full bg-white"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All sources</SelectItem>
                  <SelectItem value="contact">Contact</SelectItem>
                  <SelectItem value="journey">Journey</SelectItem>
                  <SelectItem value="resource">Resource</SelectItem>
                  <SelectItem value="booking">Booking</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="block text-xs text-slate-500 mb-1 ml-1">From</label>
              <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} data-testid="filter-date-from" className="h-11 rounded-full bg-white w-40" />
            </div>
            <div>
              <label className="block text-xs text-slate-500 mb-1 ml-1">To</label>
              <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} data-testid="filter-date-to" className="h-11 rounded-full bg-white w-40" />
            </div>
            <Button variant="ghost" onClick={resetFilters} data-testid="filter-reset" className="h-11 rounded-full text-slate-500">Reset</Button>
          </div>

          <TabsContent value="leads" className="mt-5">
            <div className="flex justify-end mb-3">
              <Button onClick={() => exportCsv("leads")} data-testid="export-leads" variant="outline" className="rounded-full"><Download size={16} className="mr-2" /> Export CSV</Button>
            </div>
            <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="leads-table">
                  <thead className="bg-slate-50 text-slate-500 text-left">
                    <tr>
                      <th className="px-5 py-3 font-medium">Name</th>
                      <th className="px-5 py-3 font-medium">Email</th>
                      <th className="px-5 py-3 font-medium">Phone</th>
                      <th className="px-5 py-3 font-medium">Source</th>
                      <th className="px-5 py-3 font-medium">Status</th>
                      <th className="px-5 py-3 font-medium">Notes</th>
                      <th className="px-5 py-3 font-medium">When</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {filteredLeads.map((l) => (
                      <tr key={l.id} className="hover:bg-slate-50" data-testid="lead-row">
                        <td className="px-5 py-3 font-medium text-[#0B3D2E]">{l.name}</td>
                        <td className="px-5 py-3 text-slate-600">{l.email}</td>
                        <td className="px-5 py-3 text-slate-600">{l.phone || "—"}</td>
                        <td className="px-5 py-3"><Badge className={`${SRC_COLORS[l.source] || "bg-slate-100 text-slate-600"} border-0 rounded-full capitalize`}>{l.source}</Badge></td>
                        <td className="px-5 py-3"><StatusSelect value={l.status} testId={`lead-status-${l.id}`} onChange={(v) => updateStatus("leads", l.id, v)} /></td>
                        <td className="px-5 py-3">
                          <Button variant="ghost" size="sm" data-testid={`lead-notes-${l.id}`} onClick={() => setNotesEdit({ type: "leads", id: l.id, name: l.name, value: l.internal_notes || "" })} className="rounded-full text-slate-500 hover:text-emerald-700">
                            <StickyNote size={15} className="mr-1.5" />{l.internal_notes ? "View" : "Add"}
                          </Button>
                        </td>
                        <td className="px-5 py-3 text-slate-400 whitespace-nowrap">{fmt(l.created_at)}</td>
                      </tr>
                    ))}
                    {filteredLeads.length === 0 && <tr><td colSpan={7} className="px-5 py-10 text-center text-slate-400">No leads match your filters.</td></tr>}
                  </tbody>
                </table>
              </div>
            </div>
          </TabsContent>

          <TabsContent value="bookings" className="mt-5">
            <div className="flex justify-end mb-3">
              <Button onClick={() => exportCsv("bookings")} data-testid="export-bookings" variant="outline" className="rounded-full"><Download size={16} className="mr-2" /> Export CSV</Button>
            </div>
            <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="bookings-table">
                  <thead className="bg-slate-50 text-slate-500 text-left">
                    <tr>
                      <th className="px-5 py-3 font-medium">Name</th>
                      <th className="px-5 py-3 font-medium">Contact</th>
                      <th className="px-5 py-3 font-medium">Service</th>
                      <th className="px-5 py-3 font-medium">Preferred</th>
                      <th className="px-5 py-3 font-medium">Status</th>
                      <th className="px-5 py-3 font-medium">Notes</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {filteredBookings.map((b) => (
                      <tr key={b.id} className="hover:bg-slate-50" data-testid="booking-row">
                        <td className="px-5 py-3 font-medium text-[#0B3D2E]">{b.name}</td>
                        <td className="px-5 py-3 text-slate-600">{b.email}<br /><span className="text-slate-400">{b.phone}</span></td>
                        <td className="px-5 py-3 text-slate-600">{b.service}</td>
                        <td className="px-5 py-3 text-slate-600">{b.date}<br /><span className="text-slate-400">{b.time}</span></td>
                        <td className="px-5 py-3"><StatusSelect value={b.status} testId={`booking-status-${b.id}`} onChange={(v) => updateStatus("bookings", b.id, v)} /></td>
                        <td className="px-5 py-3">
                          <Button variant="ghost" size="sm" data-testid={`booking-notes-${b.id}`} onClick={() => setNotesEdit({ type: "bookings", id: b.id, name: b.name, value: b.internal_notes || "" })} className="rounded-full text-slate-500 hover:text-emerald-700">
                            <StickyNote size={15} className="mr-1.5" />{b.internal_notes ? "View" : "Add"}
                          </Button>
                        </td>
                      </tr>
                    ))}
                    {filteredBookings.length === 0 && <tr><td colSpan={6} className="px-5 py-10 text-center text-slate-400">No bookings match your filters.</td></tr>}
                  </tbody>
                </table>
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </div>

      <Dialog open={!!notesEdit} onOpenChange={(o) => !o && setNotesEdit(null)}>
        <DialogContent className="rounded-3xl sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-display text-xl text-[#0B3D2E]">Internal notes {notesEdit ? `· ${notesEdit.name}` : ""}</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-slate-500 -mt-1">Private notes for your team. Record conversations, follow-ups and next steps.</p>
          <Textarea
            value={notesEdit?.value || ""}
            onChange={(e) => setNotesEdit((n) => ({ ...n, value: e.target.value }))}
            data-testid="notes-textarea"
            rows={6}
            className="rounded-xl mt-2"
            placeholder="e.g. Called 22 Jul, sent life cover quote, following up Friday…"
          />
          <DialogFooter className="mt-2">
            <Button variant="ghost" onClick={() => setNotesEdit(null)} className="rounded-full">Cancel</Button>
            <Button onClick={saveNotes} disabled={savingNotes} data-testid="notes-save" className="rounded-full bg-emerald-600 hover:bg-emerald-700 text-white px-6">{savingNotes ? "Saving…" : "Save notes"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
